import { useState, type ReactNode, type FormEvent } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, Badge, Modal, BotonAccion, formatU, formatFecha, confirmar } from '../shared/ui.tsx';
import type { Categoria, Tarifa } from '../shared/tipos.ts';

type TarifaConCat = Tarifa & { categoria_nombre: string };

export function CategoriasTarifasPage(): ReactNode {
  const [tab, setTab] = useState<'categorias' | 'tarifas'>('categorias');
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Categorías y tarifas</h2>
      <div className="flex gap-2">
        <button
          className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === 'categorias' ? 'bg-primario-400 text-white' : 'bg-superficie-200 text-neutro-800'}`}
          onClick={() => setTab('categorias')}
        >
          Categorías
        </button>
        <button
          className={`rounded-lg px-4 py-2 text-sm font-medium ${tab === 'tarifas' ? 'bg-primario-400 text-white' : 'bg-superficie-200 text-neutro-800'}`}
          onClick={() => setTab('tarifas')}
        >
          Tarifas
        </button>
      </div>
      {tab === 'categorias' ? <CategoriasPanel /> : <TarifasPanel />}
    </div>
  );
}

function CategoriasPanel(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<Categoria[]>('/api/categorias');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editar, setEditar] = useState<Categoria | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <BotonAccion onClick={() => { setEditar(null); setMostrarForm(true); }}>+ Nueva categoría</BotonAccion>
      </div>
      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos && datos.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm border border-contorno-var">
          <table className="w-full text-sm">
            <thead className="bg-superficie-100 text-left text-neutro-800">
              <tr>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold">Orden</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datos.map((c) => (
                <tr key={c.id} className="border-t border-contorno-var hover:bg-superficie-100/50">
                  <td className="px-4 py-3 font-medium text-neutro-800">
                    <button className="hover:underline" onClick={() => { setEditar(c); setMostrarForm(true); }}>{c.nombre}</button>
                  </td>
                  <td className="px-4 py-3">{c.orden}</td>
                  <td className="px-4 py-3">{c.activo ? <Badge color="verde">Activa</Badge> : <Badge color="gris">Inactiva</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    {c.activo === 1 && (
                      <button className="text-xs text-estado-error hover:underline" onClick={async () => {
                        if (confirmar(`¿Desactivar la categoría "${c.nombre}"?`)) {
                          await apiPost(`/api/categorias/${c.id}`, undefined, 'DELETE');
                          recargar();
                        }
                      }}>Desactivar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Vacio texto="No hay categorías." />}

      {mostrarForm && (
        <FormCategoria cat={editar} onCerrar={() => setMostrarForm(false)} onGuardado={() => { setMostrarForm(false); recargar(); }} />
      )}
    </div>
  );
}

function FormCategoria({ cat, onCerrar, onGuardado }: { cat: Categoria | null; onCerrar: () => void; onGuardado: () => void }): ReactNode {
  const [nombre, setNombre] = useState(cat?.nombre ?? '');
  const [orden, setOrden] = useState(cat?.orden ?? 0);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setErr(null); setGuardando(true);
    try {
      if (cat) {
        await apiPost(`/api/categorias/${cat.id}`, { nombre, orden }, 'PUT');
      } else {
        await apiPost('/api/categorias', { nombre, orden });
      }
      onGuardado();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setGuardando(false); }
  }

  return (
    <Modal abierto={true} titulo={cat ? 'Editar categoría' : 'Nueva categoría'} onCerrar={onCerrar}>
      <form onSubmit={guardar} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Nombre*</span>
          <input className="campo" required value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Orden</span>
          <input className="campo" type="number" value={orden} onChange={(e) => setOrden(parseInt(e.target.value, 10) || 0)} />
        </label>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</BotonAccion>
        </div>
      </form>
    </Modal>
  );
}

function TarifasPanel(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<TarifaConCat[]>('/api/tarifas');
  const cats = useApi<Categoria[]>('/api/categorias');
  const [mostrarForm, setMostrarForm] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutro-500">
          Al crear una tarifa nueva se cierra la anterior (vigente_hasta = hoy). Las liquidaciones viejas no cambian.
        </p>
        <BotonAccion onClick={() => setMostrarForm(true)}>+ Nueva tarifa</BotonAccion>
      </div>
      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos && datos.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm border border-contorno-var">
          <table className="w-full text-sm">
            <thead className="bg-superficie-100 text-left text-neutro-800">
              <tr>
                <th className="px-4 py-3 font-semibold">Categoría</th>
                <th className="px-4 py-3 font-semibold">Pago/hora</th>
                <th className="px-4 py-3 font-semibold">Cobro/hora</th>
                <th className="px-4 py-3 font-semibold">Margen</th>
                <th className="px-4 py-3 font-semibold">Noct.</th>
                <th className="px-4 py-3 font-semibold">Feriado</th>
                <th className="px-4 py-3 font-semibold">Mín. hs</th>
                <th className="px-4 py-3 font-semibold">Vigencia</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datos.map((t) => {
                const margen = t.cobro_hora - t.pago_hora;
                const vigente = t.vigente_hasta === null;
                return (
                  <tr key={t.id} className="border-t border-contorno-var hover:bg-superficie-100/50">
                    <td className="px-4 py-3 font-medium text-neutro-800">{t.categoria_nombre}</td>
                    <td className="px-4 py-3">{formatU(t.pago_hora)}</td>
                    <td className="px-4 py-3">{formatU(t.cobro_hora)}</td>
                    <td className="px-4 py-3 text-primario-400">{formatU(margen)}</td>
                    <td className="px-4 py-3">{t.recargo_nocturno_pct}%</td>
                    <td className="px-4 py-3">{t.recargo_feriado_pct}%</td>
                    <td className="px-4 py-3">{t.minimo_horas}h</td>
                    <td className="px-4 py-3">
                      <div className="text-xs">
                        {vigente ? <Badge color="verde">Vigente</Badge> : <Badge color="gris">Cerrada</Badge>}
                        <div className="mt-0.5 text-neutro-400">desde {formatFecha(t.vigente_desde)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {vigente && (
                        <button className="text-xs text-estado-error hover:underline" onClick={async () => {
                          if (confirmar('¿Cerrar la vigencia de esta tarifa? (no se borra)')) {
                            await apiPost(`/api/tarifas/${t.id}`, undefined, 'DELETE');
                            recargar();
                          }
                        }}>Cerrar vigencia</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Vacio texto="No hay tarifas." />}

      {mostrarForm && (
        <FormTarifa categorias={cats.datos ?? []} onCerrar={() => setMostrarForm(false)} onGuardado={() => { setMostrarForm(false); recargar(); }} />
      )}
    </div>
  );
}

function FormTarifa({ categorias, onCerrar, onGuardado }: { categorias: Categoria[]; onCerrar: () => void; onGuardado: () => void }): ReactNode {
  const [form, setForm] = useState({
    categoria_id: '',
    pago_hora: 0,
    cobro_hora: 0,
    recargo_nocturno_pct: 20,
    recargo_feriado_pct: 50,
    minimo_horas: 4,
    vigente_desde: new Date().toISOString().slice(0, 10),
  });
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (!form.categoria_id) { setErr('Elegí una categoría'); return; }
    setErr(null); setGuardando(true);
    try {
      // valores en centésimos: el backend espera centésimos, el usuario ingresa pesos.
      // Multiplicamos por 100 al enviar.
      await apiPost('/api/tarifas', {
        ...form,
        pago_hora: Math.round(form.pago_hora * 100),
        cobro_hora: Math.round(form.cobro_hora * 100),
      });
      onGuardado();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setGuardando(false); }
  }

  function set(c: keyof typeof form, v: unknown) {
    setForm({ ...form, [c]: v });
  }

  return (
    <Modal abierto={true} titulo="Nueva tarifa" onCerrar={onCerrar}>
      <form onSubmit={guardar} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Categoría*</span>
          <select className="campo" required value={form.categoria_id} onChange={(e) => set('categoria_id', e.target.value)}>
            <option value="">Elegir…</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Pago/hora ($U)*</span>
            <input className="campo" type="number" min={0} required value={form.pago_hora || ''} onChange={(e) => set('pago_hora', parseFloat(e.target.value) || 0)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Cobro/hora ($U)*</span>
            <input className="campo" type="number" min={0} required value={form.cobro_hora || ''} onChange={(e) => set('cobro_hora', parseFloat(e.target.value) || 0)} />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Recargo noct. %</span>
            <input className="campo" type="number" value={form.recargo_nocturno_pct} onChange={(e) => set('recargo_nocturno_pct', parseInt(e.target.value, 10) || 0)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Recargo feriado %</span>
            <input className="campo" type="number" value={form.recargo_feriado_pct} onChange={(e) => set('recargo_feriado_pct', parseInt(e.target.value, 10) || 0)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Mín. horas</span>
            <input className="campo" type="number" min={1} value={form.minimo_horas} onChange={(e) => set('minimo_horas', parseInt(e.target.value, 10) || 1)} />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Vigente desde*</span>
          <input className="campo" type="date" required value={form.vigente_desde} onChange={(e) => set('vigente_desde', e.target.value)} />
        </label>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</BotonAccion>
        </div>
      </form>
    </Modal>
  );
}